import { useCallback, useEffect, useRef } from 'react';
import { FieldInput, processModuleApi } from 'src/api/process-module';
import { Field } from 'src/API';
import toast from 'react-hot-toast';
import logger from 'src/log/log';

interface UseFieldAutoSaveOptions {
  organizationId: string;
  processModuleId: string;
  debounceMs?: number;
  onSaveSuccess?: (field: Field) => void;
  onSaveError?: (error: any) => void;
}

interface FieldSaveState {
  fieldId: string;
  isNew: boolean;
  data: Partial<FieldInput & Partial<Field>>;
  retryCount?: number;
  lastError?: any;
}

export const useFieldAutoSave = ({
  organizationId,
  processModuleId,
  debounceMs = 1000,
  onSaveSuccess,
  onSaveError,
}: UseFieldAutoSaveOptions) => {
  const pendingSaves = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const fieldStates = useRef<Map<string, FieldSaveState>>(new Map());

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      const currentPendingSaves = pendingSaves.current;
      currentPendingSaves.forEach((timeout) => clearTimeout(timeout));
      currentPendingSaves.clear();
    };
  }, []);

  const saveField = useCallback(
    async (fieldId: string, data: Partial<FieldInput & Partial<Field>>, isNew: boolean, retryCount: number = 0) => {
      const MAX_RETRIES = 3;
      const RETRY_DELAY_MS = 1000;

      try {
        logger.log('Saving field:', {
          fieldId,
          isNew,
          retryCount,
          dataKeys: Object.keys(data),
          dataSourceLength: typeof data.dataSource === 'string' ? data.dataSource.length : 'N/A',
        });

        // Enhanced data validation before save
        const cleanedData = {
          ...data,
          // Ensure dataSource is properly serialized for complex field types
          dataSource: data.dataSource !== undefined && data.dataSource !== null ? String(data.dataSource) : undefined,
        } as any; // Type assertion to handle dynamic properties like formId

        let result: any;
        if (isNew) {
          // Create new field with form validation
          if (!cleanedData.formId) {
            throw new Error('Form ID is required for new field creation');
          }
          result = await processModuleApi.createField(organizationId, processModuleId, cleanedData);
        } else {
          // Update existing field
          result = await processModuleApi.updateField(organizationId, fieldId, cleanedData);
        }

        // Handle different response formats
        let fieldData: Field | null = null;
        let isSuccess = false;

        if (result?.ok && result.result) {
          // New format: { ok: true, result: Field, errors: [] }
          fieldData = result.result as Field;
          isSuccess = true;
          logger.log('Field saved successfully (new format):', {
            fieldId: fieldData.id,
            type: fieldData.type,
            retryCount,
          });
        } else if (result?.ok && result.data?.field) {
          // Old format: { ok: true, data: { field: Field } }
          fieldData = result.data.field as Field;
          isSuccess = true;
          logger.log('Field saved successfully (old format):', {
            fieldId: fieldData.id,
            type: fieldData.type,
            retryCount,
          });
        } else if (result && (result as any).id) {
          // Direct field response
          fieldData = result as Field;
          isSuccess = true;
          logger.log('Field saved successfully (direct format):', {
            fieldId: fieldData.id,
            retryCount,
          });
        }

        if (isSuccess && fieldData) {
          onSaveSuccess?.(fieldData);

          // Update field state to mark as no longer new and clear retry count
          const currentState = fieldStates.current.get(fieldId);
          if (currentState) {
            fieldStates.current.set(fieldId, {
              ...currentState,
              isNew: false,
              fieldId: fieldData.id,
              retryCount: 0,
              lastError: undefined,
            });
          }
        } else {
          const errorDetails = {
            hasResult: !!result,
            hasOk: result?.ok,
            hasResultField: !!result?.result,
            hasDataField: !!result?.data?.field,
            hasDirectId: !!(result as any)?.id,
            resultKeys: result ? Object.keys(result) : [],
          };
          logger.error('Invalid API response structure:', errorDetails);
          throw new Error(`Invalid response from ${isNew ? 'create' : 'update'} field API - missing field data`);
        }
      } catch (error) {
        const currentState = fieldStates.current.get(fieldId);

        logger.error(`Error ${isNew ? 'creating' : 'updating'} field (attempt ${retryCount + 1}):`, {
          fieldId,
          error,
          retryCount,
          maxRetries: MAX_RETRIES,
        });

        // Update field state with error info
        if (currentState) {
          fieldStates.current.set(fieldId, {
            ...currentState,
            retryCount: retryCount + 1,
            lastError: error,
          });
        }

        // Retry logic for transient errors
        if (retryCount < MAX_RETRIES && isRetryableError(error)) {
          logger.log(`Retrying field save in ${RETRY_DELAY_MS}ms...`, { fieldId, retryCount: retryCount + 1 });

          setTimeout(
            () => {
              saveField(fieldId, data, isNew, retryCount + 1);
            },
            RETRY_DELAY_MS * (retryCount + 1),
          ); // Exponential backoff

          return; // Don't show error toast on retry attempts
        }

        // Final failure after retries or non-retryable error
        onSaveError?.(error);

        const errorMessage = getErrorMessage(error, isNew);
        toast.error(errorMessage);
      }
    },
    [organizationId, processModuleId, onSaveSuccess, onSaveError],
  );

  // Helper function to determine if error is retryable
  const isRetryableError = (error: any): boolean => {
    if (!error) return false;

    // Network errors
    if (error.name === 'NetworkError' || error.code === 'NETWORK_ERROR') return true;

    // HTTP status codes that indicate temporary issues
    if (error.status && [408, 429, 500, 502, 503, 504].includes(error.status)) return true;

    // Connection timeouts
    if (error.message?.toLowerCase().includes('timeout')) return true;

    return false;
  };

  // Helper function to get user-friendly error messages
  const getErrorMessage = (error: any, isNew: boolean): string => {
    const operation = isNew ? 'create' : 'save';

    if (error.status === 400) return `Invalid field data. Please check your input and try again.`;
    if (error.status === 401) return `Authentication error. Please refresh the page and try again.`;
    if (error.status === 403) return `Permission denied. You may not have access to ${operation} this field.`;
    if (error.status === 404) return `Field or form not found. The field may have been deleted.`;
    if (error.status === 409) return `Conflict error. Another user may have modified this field.`;
    if (error.status >= 500) return `Server error. Please try again in a moment.`;

    if (error.message?.toLowerCase().includes('network')) {
      return `Network error. Please check your connection and try again.`;
    }

    return `Failed to ${operation} field. Please try again.`;
  };

  const debouncedSaveField = useCallback(
    (fieldId: string, data: Partial<FieldInput & Partial<Field>>, isNew: boolean = false) => {
      // Clear existing timeout for this field
      const existingTimeout = pendingSaves.current.get(fieldId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Update field state
      fieldStates.current.set(fieldId, { fieldId, isNew, data, retryCount: 0 });

      // Set new timeout
      const timeout = setTimeout(() => {
        saveField(fieldId, data, isNew, 0);
        pendingSaves.current.delete(fieldId);
      }, debounceMs);

      pendingSaves.current.set(fieldId, timeout);
    },
    [saveField, debounceMs],
  );

  const saveFieldImmediately = useCallback(
    async (fieldId: string, data: Partial<FieldInput & Partial<Field>>, isNew: boolean = false) => {
      // Clear any pending save for this field
      const existingTimeout = pendingSaves.current.get(fieldId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        pendingSaves.current.delete(fieldId);
      }

      // Save immediately
      await saveField(fieldId, data, isNew, 0);

      // Show success toast for immediate saves only for updates (creates are handled by FormBuilder)
      if (!isNew) {
        toast.success('Field saved successfully');
      }
    },
    [saveField],
  );

  const cancelPendingSave = useCallback((fieldId: string) => {
    const existingTimeout = pendingSaves.current.get(fieldId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      pendingSaves.current.delete(fieldId);
    }
    fieldStates.current.delete(fieldId);
  }, []);

  const hasPendingSave = useCallback((fieldId: string) => {
    return pendingSaves.current.has(fieldId);
  }, []);

  const flushAllPendingSaves = useCallback(async () => {
    const promises: Promise<void>[] = [];

    fieldStates.current.forEach(({ fieldId, data, isNew }) => {
      // Cancel the timeout
      const timeout = pendingSaves.current.get(fieldId);
      if (timeout) {
        clearTimeout(timeout);
        pendingSaves.current.delete(fieldId);
      }

      // Add to promises array
      promises.push(saveField(fieldId, data, isNew, 0));
    });

    await Promise.all(promises);
    fieldStates.current.clear();
  }, [saveField]);

  return {
    debouncedSaveField,
    saveFieldImmediately,
    cancelPendingSave,
    hasPendingSave,
    flushAllPendingSaves,
  };
};
